import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getIntuneAccordionDefaultActiveKeys } from '../../intune/intune_common/intuneAccordionDefaults'
import {
  IntuneEditFormAccordion,
  IntuneEditFormAccordionItem,
} from '../../intune/intune_common/IntuneEditFormAccordion'
import InputGroup from './InputGroup'
import { getVisiblePolicyAccordionSections, POLICY_ACCORDION_SECTIONS } from './policyAccordionSections'

export const POLICY_FRAME_LAYOUT = {
  ACCORDION: 'accordion',
  FLAT: 'flat',
}

const propTypes = {
  /**
   * callback to decide if input should be displayed or not
   * If return true, input is displayed
   */
  displayInput: PropTypes.func,
  /**
   * Function to render input. Should return a react node. Params: input
   */
  renderInput: PropTypes.func.isRequired,
  /**
   * Optional renderer for content displayed next to the input label
   */
  renderLabelAction: PropTypes.func,
  /**
   * `flat` (default) for template view. Policy view/edit and template create/edit pass `accordion`.
   */
  layout: PropTypes.oneOf(Object.values(POLICY_FRAME_LAYOUT)),
  /**
   * Fields just added from Add input(s). Their section opens if it was collapsed, and the row is marked.
   */
  highlightedInputs: PropTypes.arrayOf(PropTypes.string),
  /**
   * Field errors. A collapsed section that contains one opens, so Next does not hide the message.
   */
  validationErrors: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

const EMPTY_HIGHLIGHTED_INPUTS = []
const EMPTY_VALIDATION_ERRORS = {}
const FIELD_SCROLL_DELAY_MS = 350

const inputsWithErrors = (validationErrors) =>
  Object.entries(validationErrors || {})
    .filter(([, errors]) => Array.isArray(errors) && errors.length > 0)
    .map(([input]) => input)

const sectionIdsForInputs = (inputs) =>
  POLICY_ACCORDION_SECTIONS.filter((section) => section.inputs.some((input) => inputs.includes(input))).map(
    (section) => section.sectionId,
  )

function FormattedPolicyFrame(props) {
  const {
    displayInput = () => true,
    renderInput,
    renderLabelAction = () => null,
    layout = POLICY_FRAME_LAYOUT.FLAT,
    highlightedInputs = EMPTY_HIGHLIGHTED_INPUTS,
    validationErrors = EMPTY_VALIDATION_ERRORS,
  } = props ?? {}

  const visibleSections = useMemo(() => getVisiblePolicyAccordionSections(displayInput), [displayInput])
  const sectionIds = useMemo(() => visibleSections.map((section) => section.sectionId), [visibleSections])
  const sectionIdsKey = sectionIds.join('|')
  const [trackedSectionKey, setTrackedSectionKey] = useState(sectionIdsKey)
  const [activeKeys, setActiveKeys] = useState(() => getIntuneAccordionDefaultActiveKeys(sectionIds) ?? [])

  // A newly visible section (a field added into a section that was not on the form) starts open.
  // Sections the user already opened or closed stay as they were.
  if (trackedSectionKey !== sectionIdsKey) {
    const previousIds = new Set(trackedSectionKey ? trackedSectionKey.split('|') : [])
    const nextIds = new Set(sectionIds)
    const addedSectionIds = sectionIds.filter((sectionId) => !previousIds.has(sectionId))
    setTrackedSectionKey(sectionIdsKey)
    setActiveKeys((current) => [...new Set([...current.filter((key) => nextIds.has(key)), ...addedSectionIds])])
  }

  const handleAccordionSelect = useCallback((eventKey) => {
    if (eventKey == null) {
      setActiveKeys([])
      return
    }

    setActiveKeys(Array.isArray(eventKey) ? eventKey : [eventKey])
  }, [])

  // Field messages render inside the section. PolicyValidationFallback only lists inputs that are
  // not on the form, so a collapsed section would hide the reason Next stopped.
  useEffect(() => {
    if (layout !== POLICY_FRAME_LAYOUT.ACCORDION) {
      return
    }

    const toOpen = sectionIdsForInputs(inputsWithErrors(validationErrors))
    if (!toOpen.length) {
      return
    }

    setActiveKeys((current) => {
      const next = [...new Set([...current, ...toOpen])]
      const unchanged = next.length === current.length && next.every((key, index) => key === current[index])
      return unchanged ? current : next
    })
  }, [validationErrors, layout])

  useEffect(() => {
    if (!highlightedInputs.length) {
      return undefined
    }

    if (layout === POLICY_FRAME_LAYOUT.ACCORDION) {
      const toOpen = sectionIdsForInputs(highlightedInputs)
      if (toOpen.length) {
        setActiveKeys((current) => [...new Set([...current, ...toOpen])])
      }
    }

    const fieldId = `policy-field-${highlightedInputs[0]}`
    const timeoutId = window.setTimeout(() => {
      document.getElementById(fieldId)?.scrollIntoView?.({ block: 'nearest' })
    }, FIELD_SCROLL_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [highlightedInputs, layout])

  if (layout === POLICY_FRAME_LAYOUT.FLAT) {
    return (
      <>
        {visibleSections.map((section) => (
          <InputGroup
            key={section.sectionId}
            groupTitle={section.titleMessageId}
            inputs={section.inputs}
            displayInput={displayInput}
            renderInput={renderInput}
            renderLabelAction={renderLabelAction}
            highlightedInputs={highlightedInputs}
          />
        ))}
      </>
    )
  }

  return (
    <IntuneEditFormAccordion activeKey={activeKeys} onSelect={handleAccordionSelect}>
      {visibleSections.map((section) => (
        <IntuneEditFormAccordionItem
          key={section.sectionId}
          eventKey={section.sectionId}
          titleMessageId={section.titleMessageId}
        >
          <InputGroup
            inputs={section.inputs}
            displayInput={displayInput}
            renderInput={renderInput}
            renderLabelAction={renderLabelAction}
            highlightedInputs={highlightedInputs}
          />
        </IntuneEditFormAccordionItem>
      ))}
    </IntuneEditFormAccordion>
  )
}

FormattedPolicyFrame.propTypes = propTypes

export default FormattedPolicyFrame
